import type { AttachmentUploaded } from 'types/api/attachment';
import type { SimpleDocument } from 'types/api/document';
import type {
  Lab,
  LabTrade,
  PmsPromotion,
  Trade,
  TradeInstance,
  TradeProduct,
  TradeRequirement,
  TradeRequirementInstance,
  TradeSellType,
  TradesInstanceStatus,
  TradeTag,
} from 'types/models';
import type { CompensatedBy, CompensationMethod } from 'types/models/compensation';
import type { Promotion } from 'types/models/promotion';
import type { SelloutExtractRequest } from 'types/models/sellout_extract_request';
import type { TradesInstancePayment } from 'types/models/trades_instance_payment';
import type { TradesPharmacyConfig } from 'types/models/trades_pharmacy_config';
import type { TradesSelloutPanelistRequest } from 'types/models/trades_sellout_panelist_request';

export const requirementKinds = ['mea', 'sellout', 'all'] as const;
export type RequirementKind = (typeof requirementKinds)[number];

export const tradesSources = ['faks', 'platform_connect'] as const;

export type TradeSource = (typeof tradesSources)[number];

export const labPartnershipFilters = ['partner', 'non_partner'] as const;
export type LabPartnershipFilter = (typeof labPartnershipFilters)[number];

export type RequirementItem = Pick<
  TradeRequirement,
  | 'id'
  | 'kind'
  | 'name'
  | 'description'
  | 'start_date'
  | 'end_date'
  | 'compensation_type'
  | 'compensation_amount_in_euros_cents'
  | 'compensation_permyriad'
  | 'compensation_ug_threshold'
  | 'compensation_ug_free_quantity'
  | 'compensation_method'
  | 'compensated_by'
  | 'compensation_type_other_text'
>;

export type GetTradesForLabFilters = {
  lab_id: number;
  year: number;
  query?: string;
  trades_instances_status?: TradesInstanceStatus[];
  tags_id?: number[];
  has_group_priority?: boolean;
  source?: TradeSource;
  enhanced_statuses?: PharmacyTradeEnhancedStatus[];
};

export type GetTradesForLabParams =
  | GetTradesForLabFilters
  | { lab_id: number; subscribed: boolean };

export type GetTradeForLabItem = Pick<
  GetTradeListItem,
  | 'id'
  | 'kind'
  | 'title'
  | 'start_date'
  | 'end_date'
  | 'status'
  | 'owner_type'
  | 'out_of_group_agreement'
  | 'has_group_priority'
  | 'lab'
  | 'tags'
  | 'participation'
  | 'enhanced_status'
  | 'sellout_requirement'
  | 'picture_requirement'
  | 'participation_window_extended_until'
> &
  Pick<Trade, 'lab_trade_id' | 'group_agreement'>;

export type GetTradesForLabResponse = {
  trades: GetTradeForLabItem[];
};

export type CreatePharmacyTradeParams = {
  trade: {
    title: string;
    description: string;
    start_date: string;
    end_date: string;
    lab_id: number;
  };
};

export type CreatePharmacyTradeResponse = {
  trade_id: number;
};

export type GetAccessibleNotSubscribedForLabCountParams = {
  lab_id: number;
};

export type AccessibleNotSubscribedForLabCountResponse = {
  accessible_not_subscribed_trades_for_lab_count: number;
  labs_pharmacy_id: number;
};

export type GetPharmaciesTradesPharmacyConfigResponse = Pick<
  TradesPharmacyConfig,
  'platform_connect_enabled' | 'pms_sync_enabled'
> & { should_display_konnect_banner: boolean; konnect_client: boolean };

export const ENCODABILITY_REASONS = [
  'pms_sync_not_configured',
  'pms_onboarding_not_finished',
  'pms_onboarding_verficiation_ongoing',
  'pms_synchronisation_on_error',
  'trade_no_products',
  'participation_not_found',
  'participation_from_external_source',
  'trade_is_over',
  'no_promotion',
  'promotion_type_not_currently_handled_by_faks',
  'trade_promotion_bri_or_percentage_required',
  'pms_provider_not_supported',
] as const;

export type EncodabilityReason = (typeof ENCODABILITY_REASONS)[number];

export type TradeEncodability = {
  can_be_encoded: boolean;
  non_encodability_reasons: typeof ENCODABILITY_REASONS;
};

export const ENCODING_STATES = ['encoding', 'encoded', 'error'] as const;

export type EncodingState = (typeof ENCODING_STATES)[number];

export type TradeEncodingState = { state: EncodingState } | null;

type EncodableTrade = { trade_id: number; title: string; participation_id: number | null };
type NonEncodableTrade = EncodableTrade & { non_encodability_reasons: EncodabilityReason[] };

export type BulkEncodeParams = { trade_ids: number[] };

export type BulkEncodeClassification = {
  encodable: EncodableTrade[];
  non_encodable: NonEncodableTrade[];
};

type PaymentProvider = 'stripe' | 'treezor' | 'none';

export type TradeItem = Pick<
  Trade,
  | 'id'
  | 'description'
  | 'sell_type'
  | 'kind'
  | 'mandate_contract_url'
  | 'start_date'
  | 'end_date'
  | 'title'
  | 'status'
  | 'out_of_group_agreement'
  | 'owner_type'
  | 'platform_connect_url'
  | 'participation_window_extended_until'
  | 'external_id'
  | 'additional_description'
  | 'pharmacy_accounting_document_to_generate'
  | 'faks_payment_enabled'
> & {
  lab: Pick<Lab, 'id' | 'name' | 'trade_message' | 'logo_url'> & {
    payment_provider: PaymentProvider;
  };
  lab_trade: Pick<LabTrade, 'id' | 'legal_document_to_generate'> | null;
  sellout_requirement: RequirementItem | null;
  picture_requirement: RequirementItem | null;
  documents: SimpleDocument[];
  products: Pick<
    TradeProduct,
    'id' | 'ean' | 'name' | 'vat_rate_in_percent' | 'compensation_in_cents'
  >[];
  tags: Pick<TradeTag, 'id' | 'name'>[];
  can_subscribe: boolean;
  encodability: TradeEncodability;
  pms_sync_enabled: boolean;
  ai_sellout_extraction_enabled: boolean;
  pharmacy_note: string | null;
  enhanced_status?: PharmacyTradeEnhancedStatus;
  lab_portal_id: number | null;
  lab_portal_closed: boolean;
  lab_portal_closure_message: string | null;
  promotion: Pick<
    Promotion,
    | 'promotion_type'
    | 'promotion_type_other_text'
    | 'amount_in_euros_cents'
    | 'permyriad'
    | 'ug_threshold'
    | 'ug_free_quantity'
  > | null;
};

export type ParticipationExtendedStatus =
  | TradesInstanceStatus
  | 'ongoing'
  | 'expired'
  | 'to_change';

export type RequirementWithInstance = Pick<
  TradeRequirement,
  'start_date' | 'end_date' | 'kind' | 'name' | 'id' | 'description' | 'compensation_type'
> & {
  instance:
    | (Pick<
        TradeRequirementInstance,
        | 'sellout_total_item_quantities'
        | 'sellout_total_item_sales_in_euros_cents'
        | 'pharmacy_provided_compensation_in_euro_cents'
      > & {
        id: number;
        attachments: AttachmentUploaded[];
        sellout_extract_request: Pick<SelloutExtractRequest, 'id' | 'status' | 'created_at'> | null;
      })
    | null;
};

export type RequirementWithPresentInstance = Omit<RequirementWithInstance, 'instance'> & {
  instance: NonNullable<RequirementWithInstance['instance']>;
};

export type MandateContractSignatureStatus = 'unsigned' | 'pending' | 'signed';
export type MandateContractInfo =
  | {
      kind: 'url';
      url: string;
      file_url: null;
      status: MandateContractSignatureStatus;
    }
  | {
      kind: 'file';
      url: null;
      file_url: string;
      status: MandateContractSignatureStatus;
    };

export type Participation = Pick<
  TradeInstance,
  | 'id'
  | 'status'
  | 'cannot_transmit_reason'
  | 'lab_refused_reason'
  | 'group_refused_reason'
  | 'compensation_in_euro_cents'
  | 'compensated_at'
  | 'source'
  | 'transmitted_by_id'
  | 'transmitted_at'
  | 'pharmacist_comment'
  | 'pharmacy_refused_reason'
  | 'pharmacy_refused_reason_message'
  | 'transmitted_to_platform'
  | 'validated_refused_at'
  | 'proofs_unchanged_since_refusal'
> & {
  panelist_fetch_start_date: string;
  panelist_fetch_end_date: string;
  sellout_panelist_request: TradesSelloutPanelistRequest | null;
  sellout_requirement_with_instance: RequirementWithInstance | null;
  picture_requirement_with_instance: RequirementWithInstance | null;
  is_from_external_source: boolean;
  payment: Pick<TradesInstancePayment, 'created_at'> | null;
  beneficiary_payment: {
    amount_in_cents: number;
    paid_at: string | null;
    transfer_title: string;
    iban: string;
  } | null;
  accounting_documents: (AttachmentUploaded & {
    origin?: 'digipharmacie' | null;
  })[];
  extended_status: ParticipationExtendedStatus;
  pms_promotions: PmsPromotion[];
  mandate_contract_signature: MandateContractInfo | null;
  digipharmacie_url: string | null;
};

export type PharmacyTradeEnhancedStatus =
  | 'subscribed'
  | 'fetching_sellout'
  | 'photo_to_send'
  | 'sellout_to_send'
  | 'proofs_to_send'
  | 'awaiting_validation'
  | 'validated'
  | 'paid'
  | 'refused'
  | 'expired'
  | 'archived'
  | 'not_subscribed'
  | 'closed';

export type GetTradeResponse = {
  trade: TradeItem;
  participation: Participation | null;
};

export const pharmacyTradeEnhancedStatusList = [
  'not_subscribed',
  'subscribed',
  'fetching_sellout',
  'photo_to_send',
  'sellout_to_send',
  'proofs_to_send',
  'awaiting_validation',
  'validated',
  'paid',
  'refused',
  'expired',
  'archived',
  'closed',
] as const;

export type GetTradesParams = {
  year: number;
  month: number;
  query?: string;
  enhanced_statuses?: PharmacyTradeEnhancedStatus[];
  requirement_kinds?: RequirementKind[];
  source?: TradeSource;
  tags_id?: number[];
  sell_type?: TradeSellType[];
  compensation_methods?: CompensationMethod[];
  compensated_by?: CompensatedBy[];
  lab_partnership?: LabPartnershipFilter;
};

export type ExportTradesParams = GetTradesParams & {
  trade_ids?: number[];
};

export type GetTradeListItem = Pick<
  Trade,
  | 'id'
  | 'kind'
  | 'title'
  | 'start_date'
  | 'end_date'
  | 'status'
  | 'owner_type'
  | 'out_of_group_agreement'
  | 'has_group_priority'
  | 'description'
  | 'sell_type'
> & {
  enhanced_status: PharmacyTradeEnhancedStatus;
  tags: Pick<TradeTag, 'id' | 'name'>[];
  sellout_requirement: RequirementItem | null;
  picture_requirement: RequirementItem | null;
  products: Pick<TradeProduct, 'id' | 'ean' | 'name' | 'vat_rate_in_percent'>[];
  lab: Pick<Lab, 'id' | 'name' | 'logo_url'>;
  participation:
    | (Pick<
        TradeInstance,
        | 'id'
        | 'status'
        | 'compensation_in_euro_cents'
        | 'transmitted_at'
        | 'validated_refused_at'
        | 'compensated_at'
        | 'lab_refused_reason'
        | 'group_refused_reason'
      > & {
        panelist_fetch_start_date: string;
      })
    | null;
  can_subscribe: boolean;
  participation_window_extended_until: string | null;
  encoding_state: TradeEncodingState;
  pharmacy_note: string | null;
  promotion: Pick<
    Promotion,
    | 'promotion_type'
    | 'promotion_type_other_text'
    | 'amount_in_euros_cents'
    | 'permyriad'
    | 'ug_threshold'
    | 'ug_free_quantity'
  > | null;
};

export type GetTradesResponse = {
  trades: GetTradeListItem[];
};

export type UpdatePharmacyNoteParams = {
  pharmacy_note: string | null;
};

export type SubscribeToTradeParams = {
  mandate_contract_accepted?: boolean;
};

export type SubscribeToTradeResponse = {
  participation: Pick<TradeInstance, 'id'>;
  mandate_contract: MandateContractInfo | null;
};

export type MassSubscribeToTradesParams = {
  trade_ids: number[];
};

export type GetKonnectConfigResponse = {
  konnect_client: boolean;
  onboarding_finished: boolean;
  pms_sync_enabled: boolean;
  pms_onboarding_finished: boolean;
  display_onboarding_reminder: boolean;
  lab_portal_with_errors: { id: number; name: string }[];
  pms_with_error: boolean;
};
